import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Textarea,
  Text,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { useDeclineDialogStyles } from './DeclineDialog.styles';

const MAX_REASON_LENGTH = 500;

export interface DeclineDialogProps {
  open: boolean;
  isProcessing: boolean;
  error: string | null;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export const DeclineDialog = ({
  open,
  isProcessing,
  error,
  onConfirm,
  onCancel,
}: DeclineDialogProps) => {
  const styles = useDeclineDialogStyles();
  const { t } = useTranslation('planner');
  const [reason, setReason] = useState('');
  const [validationError, setValidationError] = useState(false);

  const handleSubmit = useCallback(() => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setValidationError(true);
      return;
    }
    setValidationError(false);
    onConfirm(trimmed);
  }, [reason, onConfirm]);

  const handleCancel = useCallback(() => {
    setReason('');
    setValidationError(false);
    onCancel();
  }, [onCancel]);

  const handleReasonChange = useCallback(
    (_e: unknown, data: { value: string }) => {
      const value = data.value.slice(0, MAX_REASON_LENGTH);
      setReason(value);
      if (validationError && value.trim()) {
        setValidationError(false);
      }
    },
    [validationError]
  );

  return (
    <Dialog
      open={open}
      modalType="modal"
      onOpenChange={(_, data) => {
        if (!data.open && !isProcessing) {
          handleCancel();
        }
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>{t('reviewPanel.decline.title')}</DialogTitle>
          <DialogContent>
            <Textarea
              className={styles.textarea}
              placeholder={t('reviewPanel.decline.reasonPlaceholder')}
              value={reason}
              onChange={handleReasonChange}
              disabled={isProcessing}
              resize="vertical"
              aria-label={t('reviewPanel.decline.reasonLabel')}
              data-testid="decline-reason-textarea"
            />
            <Text className={styles.charCount}>
              {t('reviewPanel.decline.charCount', { count: reason.length })}
            </Text>
            {validationError && (
              <Text className={styles.errorText} data-testid="decline-validation-error">
                {t('reviewPanel.decline.reasonRequired')}
              </Text>
            )}
            {error && (
              <Text className={styles.errorText} data-testid="decline-api-error">
                {t('dialogs.error')}
              </Text>
            )}
          </DialogContent>
          <DialogActions>
            <BasicButton
              variant="primary"
              size="medium"
              onClick={handleSubmit}
              disabled={isProcessing || !reason.trim()}
              data-testid="decline-confirm-button"
            >
              {isProcessing
                ? t('dialogs.claim.processing')
                : t('reviewPanel.decline.confirmButton')}
            </BasicButton>
            <BasicButton
              variant="secondary"
              size="medium"
              onClick={handleCancel}
              disabled={isProcessing}
              data-testid="decline-cancel-button"
            >
              {t('reviewPanel.decline.cancelButton')}
            </BasicButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

DeclineDialog.displayName = 'DeclineDialog';
